import { useAtomValue } from 'jotai';
import { debounce, DebouncedFunc, merge } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  createFundWaterfallConfigResponse,
  createWaterfallConfig,
  FundWaterfallConfigResponse,
  PER_CLASS_PROCEEDS_KEYS,
  PerClassProceedsKey,
  UpdateWaterfallConfigResponse,
  WaterfallConfig,
  WaterfallConfigPayload,
} from '../../../data-models/fund-waterfall.data-model';
import {
  TierConfigViewModel,
  tierConfigViewModelToTierDataConfig,
  TierDataConfig,
} from '../../../schemas/FundWaterfall.schema';
import { LoadingId } from '../../../types';
import { plainDateFromDateOrDateString } from '../../../util/date-utilts';
import { selectedDateFPState, selectedFundIdStateFP } from '../state/FPState';
import { useUpdateWaterfallConfig, waterfallDataAtom } from './FPWaterfallData3';
import { CalculatedField, uniqueTierFieldKeyToPath } from './useWaterfallFormulas';

export const DebounceTime = 1000;

/**
 * Debounces `fn` behind a wrapper that is built once and always calls the newest `fn`.
 *
 * Rebuilding the wrapper whenever `fn` changes identity leaves the armed timer behind. It still
 * fires, so the debounce stops coalescing and the save it sends is built from whatever data the
 * stranded closure captured. Holding the callback in a ref keeps one timer and current data.
 */
function useStableDebounce<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => TResult,
  wait: number
): DebouncedFunc<(...args: TArgs) => TResult> {
  const latestRef = useRef(fn);

  useEffect(() => {
    latestRef.current = fn;
  }, [fn]);

  const debounced = useMemo(() => debounce((...args: TArgs) => latestRef.current(...args), wait), [wait]);

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}

export function useWaterfallConfig() {
  const fundId = useAtomValue(selectedFundIdStateFP);
  const selectedDate = useAtomValue(selectedDateFPState);
  const { data: lastSavedData } = useAtomValue(
    waterfallDataAtom({
      fundId: fundId ?? -1,
      asOfDate: plainDateFromDateOrDateString(selectedDate),
    })
  );
  const { pushErrorToast } = useToastMessageState();
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();

  const updateConfig = useUpdateWaterfallConfig();

  const updateTiers = useCallback(
    async (newConfig: TierConfigViewModel[]) => {
      if (!fundId) {
        return;
      }
      startLoading(LoadingId.updateFundWaterfallConfig);
      const payload = prepareTierConfigPayload(
        lastSavedData ?? createFundWaterfallConfigResponse({ fundId }),
        newConfig
      );
      const res = await updateConfig.mutateAsync(payload);
      if (hasValidationMessages(res.validationMessages)) {
        // these will be unexpected errors;
        // individual formula syntax validation is handled in WaterfallFormulaField, using validateWaterfallFormula
        console.error(res.validationMessages);
        pushErrorToast({ message: JSON.stringify(res.validationMessages, null, 2) });
      }
      stopLoading(LoadingId.updateFundWaterfallConfig);
    },
    [fundId, lastSavedData, pushErrorToast, startLoading, stopLoading, updateConfig]
  );

  const updateTiersDebounced = useStableDebounce(updateTiers, DebounceTime);

  const { configuration } = lastSavedData ?? {};
  const updateFormula = useCallback(
    async ({ tierField, newFormula }: { tierField: CalculatedField; newFormula: string }) => {
      if (!fundId) {
        return;
      }

      startLoading(LoadingId.updateFundWaterfallConfig);

      const payload: WaterfallConfigPayload = getUpdateFormulaPayload({
        currentConfig: configuration ?? createWaterfallConfig(),
        fundId,
        tierField,
        newFormula,
      });

      const res = await updateConfig.mutateAsync(payload);

      if (hasValidationMessages(res.validationMessages)) {
        // individual formula syntax validation will be handled inside the form/schema, using validateWaterfallFormula
        console.error(res.validationMessages);
        pushErrorToast({ message: JSON.stringify(res.validationMessages, null, 2) });
      }

      stopLoading(LoadingId.updateFundWaterfallConfig);
      return res;
    },
    [fundId, startLoading, configuration, updateConfig, stopLoading, pushErrorToast]
  );

  const updateFormulaDebounced = useStableDebounce(updateFormula, DebounceTime);

  return {
    updateWaterfallTiers: updateTiersDebounced,
    updateWaterfallFormula: updateFormulaDebounced,
    updateFormula,
  };
}

function prepareTierConfigPayload(
  lastSavedConfig: FundWaterfallConfigResponse,
  newConfig: TierConfigViewModel[]
): WaterfallConfigPayload {
  const tierData = newConfig.reduce(
    (acc, tier) => {
      acc[tier.orderNumber] = tierConfigViewModelToTierDataConfig(tier);
      return acc;
    },
    {} as Record<number, TierDataConfig>
  );
  const {
    fundId,
    configuration: { totalProceeds, lpTotalProceeds, slpTotalProceeds, gpTotalProceeds },
  } = lastSavedConfig;

  return {
    fundId,
    totalProceeds,
    lpTotalProceeds,
    slpTotalProceeds,
    gpTotalProceeds,
    tiers: tierData,
  };
}

function hasValidationMessages(validationMessages?: UpdateWaterfallConfigResponse['validationMessages']) {
  return validationMessages && Object.keys(validationMessages).length > 0;
}

function isPerClassProceedsKey(key: string): key is PerClassProceedsKey {
  return (PER_CLASS_PROCEEDS_KEYS as readonly string[]).includes(key);
}

function getUpdateFormulaPayload({
  currentConfig,
  fundId,
  newFormula,
  tierField,
}: {
  currentConfig: WaterfallConfig;
  fundId: number;
  newFormula: string;
  tierField: CalculatedField;
}): WaterfallConfigPayload {
  if (tierField.key === 'totalProceeds' || isPerClassProceedsKey(tierField.key)) {
    return { ...currentConfig, [tierField.key]: newFormula, fundId };
  }
  return merge(
    { fundId, ...currentConfig },
    {
      tiers: {
        [tierField.tierOrder]: {
          formulas: {
            [uniqueTierFieldKeyToPath(tierField.key)]: newFormula,
          },
        },
      },
    }
  );
}
